// Spec 078 — "quanto sai × quanto deveria sair", numa régua só. Puro; testado em scene-rate.test.ts.
//
// Três decisões de vocabulário, tomadas com o usuário:
//   1. "nominal" (jargão) → "esperado";
//   2. a origem da referência vira "confirmado" (par máquina × produto) ou "estimado" (padrão);
//   3. a unidade é a do parâmetro que CONTA (kg, sc, un…), não um "un" fixo — o `Un` de
//      `feedRateUnH` é contagem de peça, não grandeza física, então quem nomeia é quem mede.
//
// Cada lado da comparação carrega a SUA unidade. Quando batem, a frase fecha uma vez só
// ("600 de 800 kg/h"); quando não batem — o padrão do motor é uma contagem de peça e não herda a
// grandeza de ninguém — as duas aparecem ("600 kg/h de 60 un/h"), que é feio de propósito: é
// exatamente o sinal de que a razão está comparando coisas diferentes.
//
// As ausências continuam com nome próprio (FR-025) e NUNCA viram barra: desenhar 0% onde não há
// leitura afirmaria parada onde só há silêncio.
import { thresholdTone, type OeeTone } from "@/features/oee/oee-thresholds";
import type { OeeThresholds } from "@/hooks/useOrganizationPolicy";
import { formatSince } from "./hooks/useStaleness";
import type { SceneMachine } from "@/types/scene.types";

const rateFmt = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });
// Sem separador de milhar: em pt-BR o ponto é decimal, e "1.500%" se lê como 1,5% — justamente
// no caso (ritmo esperado cadastrado errado) em que a porcentagem estoura.
const pctFmt = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0, useGrouping: false });

/** Sem parâmetro de vazão ninguém declarou a grandeza; "un" é o menos errado, nunca vazio. */
export const FALLBACK_UNIT = "un";

/** `""`/`"  "` é tão "não declarado" quanto `null` — o `??` sozinho deixaria passar e viraria "/h". */
function unitOr(raw: string | null | undefined, fallback: string): string {
  return raw?.trim() || fallback;
}

/** A unidade do que se MEDE: o parâmetro com papel de vazão. */
export function rateUnitOf(m: SceneMachine): string {
  const f = m.flow;
  if (f.quality === "measured" || f.quality === "aged") {
    return unitOr(f.unit, expectedUnitOf(m));
  }
  return expectedUnitOf(m);
}

/** A unidade da REFERÊNCIA. `null` ⇒ padrão do motor (contagem de peça) ⇒ "un". */
export function expectedUnitOf(m: SceneMachine): string {
  return unitOr(m.nominal.unit, FALLBACK_UNIT);
}

export function formatRate(value: number, unit: string): string {
  return `${rateFmt.format(value)} ${unit}/h`;
}

/** "600 de 800 kg/h" quando a grandeza é a mesma; "600 kg/h de 60 un/h" quando não é. */
export function formatComparison(
  value: number,
  valueUnit: string,
  expected: number,
  expectedUnit: string
): string {
  return valueUnit === expectedUnit
    ? `${rateFmt.format(value)} de ${formatRate(expected, expectedUnit)}`
    : `${formatRate(value, valueUnit)} de ${formatRate(expected, expectedUnit)}`;
}

export function formatPercent(percent: number): string {
  return `${pctFmt.format(percent)}%`;
}

interface RateReference {
  /** "60 un/h" — o "deveria sair", sempre disponível (o painel o mostra mesmo sem leitura) */
  expected: string;
  provenance: "confirmado" | "estimado";
}

export type RatePresentation =
  | (RateReference & {
      kind: "measured";
      /** pode passar de 100: produzir acima do esperado é sinal legítimo, não erro */
      percent: number;
      /** os dois lados numa frase só: "600 de 800 kg/h" */
      comparison: string;
      /** leitura fora da janela viva ⇒ "14:32"; null quando a vazão está fresca */
      aged: string | null;
      tone: OeeTone;
    })
  | (RateReference & {
      kind: "absent";
      text: string;
      tone: "warn" | "muted";
      /**
       * O dot + label de status já diz isto por extenso ("Parada", "Sem dados"). O cartão da cena
       * não repete — numa máquina OCIOSA a repetição chegava a se contradizer ("Ociosa" / "parada").
       */
      saidByStatus: boolean;
    });

// Spec 079 — os limiares de cor entram por ARGUMENTO. O módulo é puro e é carregado pelo bundle
// lazy da cena 3D; conhecer a organização aqui o obrigaria a conhecer TanStack Query junto.
export function ratePresentation(m: SceneMachine, thresholds: OeeThresholds): RatePresentation {
  const f = m.flow;
  const expectedUnit = expectedUnitOf(m);
  const reference: RateReference = {
    expected: formatRate(m.nominal.rateUnH, expectedUnit),
    provenance: m.nominal.provenance === "pair" ? "confirmado" : "estimado",
  };
  const absent = (
    text: string,
    tone: "warn" | "muted",
    saidByStatus = false
  ): RatePresentation => ({
    ...reference,
    kind: "absent",
    text,
    tone,
    saidByStatus,
  });

  switch (f.quality) {
    case "measured":
    case "aged": {
      const unit = rateUnitOf(m);
      const percent = Math.round(f.ratio * 100);
      return {
        ...reference,
        kind: "measured",
        percent,
        comparison: formatComparison(f.rateUnH, unit, m.nominal.rateUnH, expectedUnit),
        aged: f.quality === "aged" ? formatSince(new Date(f.lastSampleAt)) : null,
        tone: thresholdTone(percent, thresholds),
      };
    }
    case "insufficient":
      return absent("aguardando leituras", "muted");
    case "unassigned":
      return absent("sem papel de vazão declarado", "warn");
    case "nodata":
      return absent("sem dados", "muted", true);
    default:
      return absent("parada", "muted", true);
  }
}
